import { apiClient } from '@/services/apiClient';
import type { SiteLocation, PaginatedResponse } from '@/types';

export interface SiteListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  state?: string;
  divisionCode?: string;
  regionCode?: string;
  isActive?: boolean;
  merakiDeploymentStatus?: string;
  siteType?: string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface CreateSiteInput {
  siteNumber: string;
  branchName: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  divisionCode?: string | null;
  divisionName?: string | null;
  regionCode?: string | null;
  regionName?: string | null;
  realEstateStatus?: string | null;
  leaseStartDate?: string | null;
  leaseExpirationDate?: string | null;
  merakiDeploymentStatus?: string | null;
  isActive?: boolean;
  lanIPAddressing?: string | null;
  switchDetails?: string | null;
}

export type UpdateSiteInput = Partial<Omit<CreateSiteInput, 'siteNumber'>>;

export interface EmployeeCategoryBreakdown {
  category: string;
  count: number;
}

export interface MerakiDeviceSummary {
  total: number;
  online: number;
  offline: number;
  alerting: number;
  byProductType: Array<{ productType: string; count: number }>;
  devices: unknown[];
  networkId: string | null;
}

export interface UplinkStatusSummary {
  wan1Status: string | null;
  wan1Provider: string | null;
  wan1PublicIp: string | null;
  wan2Status: string | null;
  wan2Provider: string | null;
  wan2PublicIp: string | null;
  cellularStatus: string | null;
  cellularProvider: string | null;
  hasRedundancy: boolean;
}

export interface SubnetEntry {
  subnetRange: string;
  vlanId: string | null;
  networkName: string | null;
}

export interface SiteSpendSummary {
  totalSpend: number;
  invoiceCount: number;
  circuitMRC: number;
  topVendors: Array<{ vendorName: string; totalAmount: number }>;
}

export interface SiteDetailResponse {
  site: SiteLocation;
  contracts: unknown[];
  circuits: unknown[];
  assets: unknown[];
  branchRankings: unknown[];
  employeeHeadcount: number;
  employeeCategoryBreakdown: EmployeeCategoryBreakdown[];
  employees: Array<{
    employeeId: number;
    legalName: string;
    jobTitle: string | null;
    businessTitle: string | null;
    workEmail: string | null;
    employeeCategory: string | null;
  }>;
  merakiDevices: MerakiDeviceSummary;
  uplinkStatus: UplinkStatusSummary | null;
  subnets: SubnetEntry[];
  spendSummary: SiteSpendSummary;
}

export interface BulkImportResult {
  imported: number;
  updated: number;
  errors: Array<{ siteNumber: string; error: string }>;
}

export const siteService = {
  list(params?: SiteListParams): Promise<PaginatedResponse<SiteLocation>> {
    return apiClient.get('sites', params as Record<string, string | number | boolean | undefined>);
  },

  getByNumber(siteNumber: string): Promise<SiteDetailResponse> {
    return apiClient.get(`sites/${siteNumber}`);
  },

  create(input: CreateSiteInput): Promise<SiteLocation> {
    return apiClient.post('sites', input);
  },

  update(siteNumber: string, input: UpdateSiteInput): Promise<SiteLocation> {
    return apiClient.put(`sites/${siteNumber}`, input);
  },

  delete(siteNumber: string): Promise<void> {
    return apiClient.delete(`sites/${siteNumber}`);
  },

  bulkImport(sites: CreateSiteInput[]): Promise<BulkImportResult> {
    return apiClient.post('sites/import', { sites });
  },
};
